// src/views/proprio/ProprioFicheView.jsx — fiche du propriétaire (lecture).
//
// Vue lecture seule : identité du compte (email), liste des LOGEMENTS rattachés
// (id/nom via proprio_logements, cloisonné logIds), et rappel du cadre (conciergerie,
// loi Hoguet). Pas d'édition (les modifs de fiche restent gérées par FBZ). On
// n'expose ici AUCUNE donnée sensible (pas d'IBAN, pas de taux d'un autre proprio).
//
// EXCEPTION UNIQUE à la lecture seule (LOT D, décision Dimitri 2026-08-01) :
// la carte « Signature des contrats » permet d'accepter/révoquer le consentement
// à l'apposition automatique du nom du proprio sur les contrats des résas
// DIRECTES (trace horodatée serveur, table proprio_signature_consents).
// FAIL-CLOSED : état inconnu / erreur → case affichée DÉCOCHÉE.

import { useState, useEffect } from 'react'
import FeuilleConfirmation from '../../components/FeuilleConfirmation'
import { User, Home, PenLine } from 'lucide-react'
import { DS } from '../../lib/ui'
import {
  fetchProprioLogements,
  fetchProprioSignatureConsent,
  setProprioSignatureConsent,
} from '../../lib/proprio-api'
import { card, SectionTitle, ReadOnlyHint, ErrorBox } from './_proprioUi'

export default function ProprioFicheView({ currentUser }) {
  // ⚠️ `error` N'EST PAS DÉCORATIF (revue 2026-09-28). Sans lui, le `.catch`
  // ci-dessous rendait une liste vide et la carte affichait « Aucun logement
  // rattaché. » — un MENSONGE plausible : le propriétaire ouvre « Mon bien »
  // sur une réponse illisible, voit une page normale, et repart convaincu
  // qu'il n'a plus de logement. C'était la seule des sept vues proprio sans
  // état d'erreur ; les six autres montrent déjà l'ErrorBox.
  const [logs, setLogs] = useState({ loading: true, list: [], error: null })
  // LOT D — consentement signature. FAIL-CLOSED : état initial / inconnu /
  // erreur = { applicable:false } côté chargement puis case DÉCOCHÉE (jamais
  // une case cochée sans preuve serveur).
  const [consent, setConsent] = useState({ loading: true, applicable: false, active: false, acceptedAt: null, text: '', saving: false, error: null })

  useEffect(() => {
    let alive = true
    fetchProprioLogements()
      .then((r) => { if (alive) setLogs({ loading: false, list: r.logements || [], error: null }) })
      .catch((e) => {
        console.error('[proprio] fiche logements', e?.message || e)
        if (alive) setLogs({ loading: false, list: [], error: e?.message || 'Une erreur est survenue.' })
      })
    fetchProprioSignatureConsent()
      .then((r) => {
        if (!alive) return
        setConsent((c) => ({
          ...c,
          loading: false,
          applicable: !!r.applicable,
          // fail-closed : état 'unknown' (table pas migrée / erreur DB) → décoché.
          active: r.state === 'unknown' ? false : !!r.active,
          acceptedAt: r.accepted_at || null,
          text: r.consent_text || '',
        }))
      })
      .catch(() => { if (alive) setConsent((c) => ({ ...c, loading: false, applicable: false, active: false })) })
    return () => { alive = false }
  }, [])

  const CONSENT_LABEL = "J'autorise l'apposition automatique de ma signature sur les contrats de location générés pour mes réservations en direct"

  // ⚠️ PLUS DE `window.confirm` (Dimitri 2026-08-22) : la révocation demande sa
  // confirmation dans NOTRE grammaire — feuille qui monte sur téléphone, carte
  // centrée sur ordinateur (FeuilleConfirmation).
  const [confirmRevocation, setConfirmRevocation] = useState(false)

  function onToggleConsent() {
    if (consent.saving) return
    if (consent.active) { setConfirmRevocation(true); return }
    ecrireConsent(true)
  }

  async function ecrireConsent(next) {
    setConfirmRevocation(false)
    setConsent((c) => ({ ...c, saving: true, error: null }))
    try {
      const r = await setProprioSignatureConsent(next)
      setConsent((c) => ({
        ...c,
        saving: false,
        active: !!r.active,
        acceptedAt: r.accepted_at || (r.active ? c.acceptedAt : null),
      }))
    } catch (e) {
      // Fail-closed : on ne présume JAMAIS l'écriture réussie — on garde l'état
      // précédent et on affiche l'erreur. Revue m5 : jamais un code technique
      // brut seul — message FR lisible ; le code serveur reste en détail entre
      // parenthèses. Les messages déjà humains (ex. « Connexion impossible… »)
      // passent tels quels.
      const msg = e.message || ''
      const isCode = /^[a-z0-9_]+$/.test(msg)
      const KNOWN = {
        consent_store_unavailable: 'Fonction pas encore disponible, réessayez plus tard.',
        proprio_scope_unresolved: 'Compte non rattaché à un propriétaire unique — contactez votre interlocuteur FBZ.',
        not_applicable: 'Sans objet pour ce compte.',
      }
      const friendly = KNOWN[msg] || (isCode || !msg ? 'Enregistrement impossible. Réessayez.' : msg)
      setConsent((c) => ({
        ...c,
        saving: false,
        error: isCode ? `${friendly} (détail technique : ${msg})` : friendly,
      }))
    }
  }

  const feuilleRevocation = confirmRevocation ? (
    <FeuilleConfirmation
      testId="proprio-revocation"
      titre="Révoquer l'autorisation de signature ?"
      texte="Les prochains contrats de vos réservations en direct partiront sans votre nom dans le bloc signature, et FULLBNBZEN vous contactera pour une signature manuelle. L'autorisation déjà donnée reste tracée."
      libelleConfirmer="Révoquer"
      libelleRenoncer="Garder l'autorisation"
      occupe={consent.saving}
      onConfirmer={() => ecrireConsent(false)}
      onRenoncer={() => setConfirmRevocation(false)} />
  ) : null

  return (
    <div>
      <SectionTitle icon={User} sub="Informations de votre compte.">Ma fiche</SectionTitle>
      {feuilleRevocation}
      <ReadOnlyHint />

      <div style={card}>
        <Row label="Email du compte" value={currentUser?.email || '—'} />
        <Row label="Rôle" value="Propriétaire" />
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, color: DS.textMuted, marginBottom: 6 }}>Vos logements</div>
        {logs.loading && <div style={{ fontSize: 13, color: DS.textMuted }}>Chargement…</div>}
        {/* L'ERREUR PASSE AVANT LE VIDE : « aucun logement » ne se dit QUE si
            la réponse est arrivée et qu'elle est bien vide. */}
        {!logs.loading && logs.error && <ErrorBox message={logs.error} />}
        {!logs.loading && !logs.error && logs.list.length === 0 && (
          <div style={{ fontSize: 13, color: DS.textMuted }}>Aucun logement rattaché.</div>
        )}
        {!logs.loading && !logs.error && logs.list.map((l) => (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: `1px solid ${DS.border}` }}>
            <span style={{ flex: '0 0 auto', width: 26, height: 26, borderRadius: 7, background: DS.cielSoft, color: DS.marine, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Home size={15} strokeWidth={1.8} /></span>
            <span style={{ fontSize: 13, fontWeight: 600, color: DS.marine }}>{l.nom_complet || l.nom}</span>
          </div>
        ))}
      </div>

      {/* LOT D — consentement signature (résas directes). Carte masquée si non
          applicable (compte sans proprio dérivable / Maison Zen). Fail-closed :
          chargement ou erreur → case décochée. */}
      {!consent.loading && consent.applicable && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ flex: '0 0 auto', width: 26, height: 26, borderRadius: 7, background: DS.cielSoft, color: DS.marine, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><PenLine size={15} strokeWidth={1.8} /></span>
            <span style={{ fontSize: 12, color: DS.textMuted }}>Signature des contrats — réservations en direct</span>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: consent.saving ? 'wait' : 'pointer' }}>
            <input
              type="checkbox"
              checked={consent.active}
              disabled={consent.saving}
              onChange={onToggleConsent}
              style={{ marginTop: 3, flex: '0 0 auto' }}
            />
            {/* Libellé servi par le serveur (source unique lib/proprio-consent.js
                CONSENT_TEXT — c'est le texte réellement tracé en base) ; repli
                local IDENTIQUE si la réponse ne le porte pas. */}
            <span style={{ fontSize: 13, color: DS.textPrimary, lineHeight: 1.5 }}>{consent.text || CONSENT_LABEL}</span>
          </label>
          {consent.active && consent.acceptedAt && (
            <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 8 }}>
              Autorisation acceptée le {new Date(consent.acceptedAt).toLocaleDateString('fr-FR')}. Vous pouvez la révoquer à tout moment en décochant la case.
            </div>
          )}
          {!consent.active && (
            <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 8 }}>
              Sans autorisation, vos réservations en direct restent possibles : le contrat part sans votre nom dans le bloc signature et FULLBNBZEN vous contacte pour une signature manuelle.
            </div>
          )}
          {consent.error && (
            <div style={{ fontSize: 12, color: DS.danger || '#b3261e', marginTop: 8 }}>{consent.error}</div>
          )}
        </div>
      )}

      <div style={{ ...card, background: DS.cielSoft, border: `0.5px solid ${DS.ciel}66` }}>
        <div style={{ fontSize: 13, color: DS.textPrimary, lineHeight: 1.5 }}>
          FULLBNBZEN agit en <strong>conciergerie</strong> pour vos
          logements en location courte durée. Pour toute mise à jour de vos informations
          (coordonnées, coordonnées bancaires), contactez votre interlocuteur FBZ.
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '8px 0', borderBottom: `1px solid ${DS.border}` }}>
      <span style={{ fontSize: 12, color: DS.textMuted }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: DS.marine, wordBreak: 'break-word', textAlign: 'right' }}>{value}</span>
    </div>
  )
}
